import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import { surveysApi } from '../features/surveys/api.ts';
import { SurveyAnswerForm } from '../components/SurveyAnswerForm.tsx';

interface ApiError { response?: { data?: { error?: string } } }

export function SurveyCollect(): JSX.Element {
  const { surveyId } = useParams({ from: '/surveys/$surveyId/collect' });
  const [success, setSuccess] = useState(false);
  // Bumped after each save to remount the form blank (and re-seed PRIORITY)
  // ready for the next person.
  const [formKey, setFormKey] = useState(0);

  const { data: survey, isLoading, isError } = useQuery({
    queryKey: ['survey', surveyId],
    queryFn: () => surveysApi.get(surveyId),
  });

  const submit = useMutation({
    mutationFn: (answers: Record<string, unknown>) => surveysApi.submitResponse(surveyId, answers),
    onSuccess: () => {
      setSuccess(true);
      setFormKey((k) => k + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    onError: () => setSuccess(false),
  });

  if (isLoading) {
    return (
      <div className="page-narrow">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (isError || !survey) {
    return (
      <div className="page-narrow">
        <div className="card-paper flex flex-col items-center gap-3 p-10 text-center">
          <Icon name="assignment" size={32} className="text-muted-foreground" />
          <h1 className="font-serif text-xl">This survey isn’t open</h1>
          <p className="max-w-sm text-sm text-muted-foreground">
            It may have been closed or isn’t accepting responses yet. Check with your council admin.
          </p>
        </div>
      </div>
    );
  }

  const apiErrCode = (submit.error as ApiError | null)?.response?.data?.error;

  return (
    <div className="page-narrow space-y-5">
      <header className="space-y-3">
        <p className="status-label">Field collection</p>
        <h1 className="font-serif text-2xl font-bold leading-tight">{survey.title}</h1>
        {survey.description && (
          <p className="text-sm leading-relaxed text-muted-foreground">{survey.description}</p>
        )}
      </header>

      {success && (
        <div
          className="flex items-center gap-3 rounded-2xl bg-[hsl(var(--success-container))] p-4 text-[hsl(var(--on-success-container))]"
          role="status"
        >
          <Icon name="check_circle" size={24} fill className="flex-none" />
          <div className="text-sm">
            <p className="font-medium">Response saved</p>
            <p className="opacity-80">The form below is cleared and ready for the next person.</p>
          </div>
        </div>
      )}

      {submit.isError && (
        <p className="text-sm text-destructive" role="alert">
          {apiErrCode === 'survey_not_active'
            ? 'This survey isn’t accepting responses right now.'
            : 'Couldn’t save the response. Check your connection and try again.'}
        </p>
      )}

      {/* Any interaction with the form dismisses the prior success banner —
          matching the immediate feedback the enumerator had before. */}
      <div onInput={() => setSuccess(false)}>
        <SurveyAnswerForm
          key={formKey}
          questions={survey.questions}
          initialAnswers={{}}
          submitLabel="Save response"
          onSubmit={(answers) => submit.mutate(answers)}
          pending={submit.isPending}
        />
      </div>
    </div>
  );
}
